import { qboChargeRowDedupeKey } from './qboChargeMapping'
import { parseGraphMonthLabel } from './dateConverter'

// Graph label → YYYY-MM bucket key (handles YYYY-MM-DD, YYYY-MM, "Mar 2025", etc.)
const formatMonthLabelKey = (label) => {
  const s = String(label || '').trim()
  if (/^\d{4}-\d{2}/.test(s)) return s.slice(0, 7)
  const parsed = parseGraphMonthLabel(s)
  if (parsed && !Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear()
    const m = String(parsed.getMonth() + 1).padStart(2, '0')
    return `${y}-${m}`
  }
  return s.slice(0, 7)
}

// YYYY-MM (or YYYY-MM-…) → short month + year for display
const formatMonthKeyShortYear = (monthKeyOrYmd) => {
  const s = String(monthKeyOrYmd || '').trim()
  const m = /^(\d{4})-(\d{2})/.exec(s)
  if (!m) return s
  const y = Number(m[1])
  const monthIdx = parseInt(m[2], 10) - 1
  if (!Number.isFinite(y) || monthIdx < 0 || monthIdx > 11) return s
  return new Date(y, monthIdx, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

const normalizeChargeLabel = (value) => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim().toLowerCase()
  return trimmed.replace(/\u2013|\u2014/g, '-').replace(/\s+/g, ' ')
}

// Every QBO line in the month bucket (billing drawer uses this).
const sumBucketCharges = (monthlyData) => {
  if (!monthlyData || typeof monthlyData !== 'object') return 0
  const chargeLines = Array.isArray(monthlyData.Charges) ? monthlyData.Charges : []
  return chargeLines.reduce((total, line) => total + (Number(line?.amount) || 0), 0)
}

// {} = no prefs yet (all lines). Non-empty map with every value false = all types off → $0 on graph.
const chargeCategoriesAllOff = (selectedCharges) => {
  const entries = Object.entries(selectedCharges || {})
  if (!entries.length) return false
  return !entries.some(([, isOn]) => Boolean(isOn))
}

// Same bucket, but only lines whose category checkbox is on (empty selection = all lines).
// Optional `excludedChargeRowKeys`: Record<dedupe key, true> — rows removed from the graph while their category stays on.
const sumBucketChargesForGraph = (monthlyData, selectedCharges, excludedChargeRowKeys) => {
  if (!monthlyData || typeof monthlyData !== 'object') return 0
  if (chargeCategoriesAllOff(selectedCharges)) return 0
  const chargeLines = Array.isArray(monthlyData.Charges) ? monthlyData.Charges : []
  const excluded =
    excludedChargeRowKeys && typeof excludedChargeRowKeys === 'object' ? excludedChargeRowKeys : null
  const normalizedSelectedLabels = Object.entries(selectedCharges || {})
    .filter(([, isOn]) => Boolean(isOn))
    .map(([chargeName]) => normalizeChargeLabel(chargeName))
    .filter(Boolean)
  const lineExcluded = (line) => Boolean(excluded?.[qboChargeRowDedupeKey(line)])
  if (!normalizedSelectedLabels.length) {
    return chargeLines.reduce((total, line) => {
      if (lineExcluded(line)) return total
      return total + (Number(line?.amount) || 0)
    }, 0)
  }
  return chargeLines.reduce((total, line) => {
    if (lineExcluded(line)) return total
    const normalizedCategory = normalizeChargeLabel(line?.chargeCategory || '')
    if (!normalizedCategory) return total
    const categoryIsSelected = normalizedSelectedLabels.some((selected) => selected === normalizedCategory)
    if (!categoryIsSelected) return total
    return total + (Number(line?.amount) || 0)
  }, 0)
}

// Line count for the same rules as sumBucketChargesForGraph.
const countBucketChargesForGraph = (monthlyData, selectedCharges, excludedChargeRowKeys) => {
  if (!monthlyData || typeof monthlyData !== 'object') return 0
  if (chargeCategoriesAllOff(selectedCharges)) return 0
  const chargeLines = Array.isArray(monthlyData.Charges) ? monthlyData.Charges : []
  const excluded =
    excludedChargeRowKeys && typeof excludedChargeRowKeys === 'object' ? excludedChargeRowKeys : null
  const lineExcluded = (line) => Boolean(excluded?.[qboChargeRowDedupeKey(line)])
  const normalizedSelectedLabels = Object.entries(selectedCharges || {})
    .filter(([, isOn]) => Boolean(isOn))
    .map(([chargeName]) => normalizeChargeLabel(chargeName))
    .filter(Boolean)
  const passes = (line) => {
    if (lineExcluded(line)) return false
    if (!normalizedSelectedLabels.length) return true
    const normalizedCategory = normalizeChargeLabel(line?.chargeCategory || '')
    if (!normalizedCategory) return false
    return normalizedSelectedLabels.some((selected) => selected === normalizedCategory)
  }
  return chargeLines.filter(passes).length
}

const getBillableMode = (billableDiff) => {
  const billable = Boolean(billableDiff?.billable)
  const nonbillable = Boolean(billableDiff?.nonBillable ?? billableDiff?.nonbillable)

  if (billable && nonbillable) return 'all'
  if (billable) return 'billable'
  if (nonbillable) return 'nonbillable'

  return 'all'
}

const isVulnerability = (item, graphFilters) => item?.supportType === graphFilters.VULNERABILITIES

const unitValue = (item, graphFilters) => {
  if (!item || typeof item !== 'object') return 0
  if (isVulnerability(item, graphFilters)) return 1
  return Number(item.revisedSP) || 0
}

const sumSupportUnits = (items, graphFilters) => {
  if (!Array.isArray(items)) return 0
  return items.reduce((sum, item) => {
    if (!item || typeof item !== 'object') return sum
    if (isVulnerability(item, graphFilters)) return sum
    return sum + (Number(item.revisedSP) || 0)
  }, 0)
}

const sumVulnerabilityUnits = (items, graphFilters) => {
  if (!Array.isArray(items)) return 0
  return items.reduce((sum, item) => {
    if (!item || typeof item !== 'object') return sum
    return sum + (isVulnerability(item, graphFilters) ? 1 : 0)
  }, 0)
}

const sumAiUnits = (items) => {
  const list = Array.isArray(items) ? items : []
  return list.reduce((sum, item) => {
    if (!item || typeof item !== 'object') return sum
    return sum + (Number(item.revisedSP) || 0)
  }, 0)
}

// Support tickets only; vulns excluded from SP split here
const splitBillableSupportUnits = (items, graphFilters) => {
  if (!Array.isArray(items)) return { billable: 0, nonBillable: 0 }

  return items.reduce((acc, item) => {
    if (!item || typeof item !== 'object') return acc
    if (isVulnerability(item, graphFilters)) return acc

    const u = Number(item.revisedSP) || 0
    if (u <= 0) return acc

    const isBillable = item.isBillable === true
    if (isBillable) acc.billable += u
    else acc.nonBillable += u

    return acc
  }, { billable: 0, nonBillable: 0 })
}

// Support + upgrades + vulns; vulns always non-billable; missing isBillable ⇒ non-billable
const splitBillableAllUnits = (items, graphFilters) => {
  if (!Array.isArray(items)) return { billableUnits: 0, nonBillableUnits: 0 }

  return items.reduce((acc, item) => {
    if (!item || typeof item !== 'object') return acc

    const u = unitValue(item, graphFilters)
    if (u <= 0) return acc

    const isBillable =
      !isVulnerability(item, graphFilters) &&
      typeof item.isBillable === 'boolean' &&
      item.isBillable === true

    if (isBillable) acc.billableUnits += u
    else acc.nonBillableUnits += u

    return acc
  }, { billableUnits: 0, nonBillableUnits: 0 })
}

/**
 * Map time spent (hours) to story points:
 * <1 or 1 hr → 1 SP; >1 and <3 → 3 SP; 3–5 → 5 SP; >5 and ≤8 → 8 SP; >8 → 13 SP.
 */
const hoursToSP = (hours) => {
  const hrs = Number(hours)
  if (!Number.isFinite(hrs) || hrs <= 0) return 0
  if (hrs <= 1) return 1
  if (hrs < 3) return 3
  if (hrs <= 5) return 5
  if (hrs <= 8) return 8
  return 13
}

/**
 * Compute total revised SP for a ticket:
 * - If timeSpent (timeSpentHrs or timeSpent) is present and > 0: use hoursToSP(timeSpent).
 * - Otherwise: use storyPointsActual (or storyPointsEstimated) as number.
 */
const computeRevisedSPFromItem = (item) => {
  if (!item || typeof item !== 'object') return 0
  const timeSpent = Number(item.timeSpentHrs ?? item.timeSpent ?? 0)
  const spActual = Number(item.storyPointsActual)
  const spEstimated = Number(item.storyPointsEstimated)
  if (Number.isFinite(timeSpent) && timeSpent > 0) return hoursToSP(timeSpent)
  if (Number.isFinite(spActual) && spActual > 0) return Math.trunc(spActual)
  if (Number.isFinite(spEstimated) && spEstimated > 0) return Math.trunc(spEstimated)
  return 0
}

// Deficit = effective $/SP × nonBillableUnits (revenue we would have made if non-billable work had been billed)
// rate = dollars / billableUnits (when billable > 0), else dollars / (billable + nonbillable) as fallback
const computeDeficit = (dollars, billableUnits, nonBillableUnits) => {
  const d = Number(dollars) || 0
  const b = Number(billableUnits) || 0
  const n = Number(nonBillableUnits) || 0

  if (d <= 0 || n <= 0) return 0

  const denom = b > 0 ? b : (b + n)
  const rate = d / (denom > 0 ? denom : 1)

  return rate * n
}

/**
 * Full breakdown for "money we're losing on non-billable work":
 * - totalDollars: revenue we made
 * - effectiveDollarsPerSP: our rate ($/SP) from billable work; fallback to blended if no billable
 * - billableSP, nonBillableSP: story points split
 * - revenueLostNonBillable: effectiveDollarsPerSP × nonBillableSP (potential revenue not captured)
 */
const getNonBillableImpact = (dollars, billableUnits, nonBillableUnits) => {
  const d = Number(dollars) || 0
  const b = Number(billableUnits) || 0
  const n = Number(nonBillableUnits) || 0

  const totalSP = b + n
  const effectiveDollarsPerSP = totalSP > 0 ? d / totalSP : 0
  const rateFromBillable = b > 0 ? d / b : effectiveDollarsPerSP
  const revenueLostNonBillable = n > 0 ? rateFromBillable * n : 0

  return {
    totalDollars: d,
    effectiveDollarsPerSP: rateFromBillable,
    billableSP: b,
    nonBillableSP: n,
    revenueLostNonBillable
  }
}

export {
  formatMonthLabelKey,
  formatMonthKeyShortYear,
  sumBucketCharges,
  sumBucketChargesForGraph,
  countBucketChargesForGraph,
  chargeCategoriesAllOff,
  normalizeChargeLabel,
  getBillableMode,
  unitValue,
  sumSupportUnits,
  sumVulnerabilityUnits,
  sumAiUnits,
  splitBillableSupportUnits,
  splitBillableAllUnits,
  computeDeficit,
  getNonBillableImpact,
  hoursToSP,
  computeRevisedSPFromItem
}
